/* eslint-disable no-param-reassign */
import moment from 'moment';

/*
 * AddHours(time, hours)
 *
 * This function add the specified number of hours to the specified time parameter.
 *
 * SAMPLES
 * @my_time = 11:22
 * AddHours(@my_time, 3) -> 14:22
 *
 * INPUT
 * time: The time you want to use. Must be a time object.
 * houra: The number of hours to add.
 *
 * OUTPUT
 * Returns the result time of adding the specified hours to the source time param.
 */
function AddHours(time, hours) {
  if (time === null || time === undefined || time.length === 0) {
    return '';
  }

  if (time.length === 5) {
    time += ':00';
  }

  if (moment(time).isValid()) {
    return moment(time).add(hours, 'hours').format('HH:mm:ss');
  }

  return moment(`2024-01-01T${time}`).add(hours, 'hours').format('HH:mm:ss');
}

/*
 * AddMinutes(time, minutes)
 *
 * This function add the specified number of minutes to the specified time parameter.
 *
 * SAMPLES
 * @my_time = 11:22
 * AddMinutes(@my_time, 3) -> 14:22
 *
 * INPUT
 * time: The time you want to use. Must be a time object.
 * houra: The number of minutes to add.
 *
 * OUTPUT
 * Returns the result time of adding the specified minutes to the source time param.
 */
function AddMinutes(time, minutes) {
  if (time === null || time === undefined || time.length === 0) {
    return '';
  }

  if (time.length === 5) {
    time += ':00';
  }

  if (moment(time).isValid()) {
    return moment(time).add(minutes, 'minutes').format('HH:mm:ss');
  }

  return moment(`2024-01-01T${time}`).add(minutes, 'minutes').format('HH:mm:ss');
}

/*
 * TimePart(time, part, format)
 *
 * This functions returns the time part of given time
 *
 * SAMPLES
 * TimePart("12:44","hour") -> 12
 * TimePart("12:44","minute") -> 44
 *
 * INPUT
 * time: It must be a valid time
 * part: The time part you want to get.
 *     Allowed values
 *     - hour = hour
 *     - minute = Minute
 *     - second = Second
 * format: Optional. The supply time format. Default value is 'HH:mm:ss'
 *
 * OUTPUT
 * Returns the current time part
 */
function TimePart(time, part, format) {
  if (time === null || time === undefined || time.length === 0) {
    return '';
  }

  if (format === null || format === undefined || format.length === 0) {
    if (time.length === 5) {
      time += ':00';
    }
    format = 'HH:mm:ss';
  }
  var parsedTime = moment(time, format).toDate();

  switch (part) {
    case 'hour':
      return parsedTime.getHours();

    case 'minute':
      return parsedTime.getMinutes();

    case 'second':
      return parsedTime.getSeconds();

    default:
      return '';
  }
}

/*
 * TimeDiff(startTime, endTime, unit, format)
 *
 * This functions returns the time diff
 *
 * SAMPLES
 * TimeDiff("12:44","17:"44","hour") -> 5
 *
 * INPUT
 * startTime: It must be a valid time
 * endTime: It must be a valid time
 * unit: The time diff part you want to get.
 *     Allowed values
 *     - hours = Hours
 *     - minutes = Minutes
 *     - seconds = Seconds
 * format: Optional. The supply time format. Default value is 'HH:mm:ss'
 *
 * OUTPUT
 * Returns the current time diff
 */
function TimeDiff(startTime, endTime, unit = 'hours', format = '') {
  if (startTime === null || startTime === undefined || startTime.length === 0) {
    return 0;
  }
  if (endTime === null || endTime === undefined || endTime.length === 0) {
    return 0;
  }

  if (unit === null || unit === undefined || unit.length === 0) {
    unit = 'hours';
  }

  if (format === null || format === undefined || format.length === 0) {
    if (startTime.length === 5) {
      startTime += ':00';
    }
    if (endTime.length === 5) {
      endTime += ':00';
    }
    format = 'HH:mm:ss';
  }
  return moment(startTime, format).diff(moment(endTime, format), part);
}

export default {
  AddHours,
  AddMinutes,
  TimePart,
  TimeDiff,
};
